import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { transformBillFromServer } from '../../common/utils/billUtils';
import { requestBuilder } from '../../http/RequestBuilder';
import { activeBillsAtom, allBillsAtom } from '../state/BillsAtoms';
import { BillActivationStatus, type PartialBill } from '../types/Bill';

interface GetBillsRequest {
    membershipId: string;
    billActivationStatus?: BillActivationStatus;
}

const getEndpoint = (
    membershipId: string,
    billActivationStatus?: BillActivationStatus,
) => {
    return `/api/holistic-bills/${membershipId}/bills?billActivationStatus=${billActivationStatus}`;
};

const getQueryKey = (
    membershipId: string,
    billActivationStatus?: BillActivationStatus,
) => {
    return `getBills/${membershipId}/${billActivationStatus}`;
};

const completeFetchedBills = (fetchedBills: PartialBill[]) => {
    return fetchedBills.map(transformBillFromServer);
};

const useGetBills = requestBuilder.query<PartialBill[], GetBillsRequest>({
    endpoint: ({ membershipId, billActivationStatus }) =>
        getEndpoint(membershipId, billActivationStatus),
    queryKey: ({ membershipId, billActivationStatus }) =>
        getQueryKey(membershipId, billActivationStatus),
});

export const useGetActiveBills = (
    useStaleTime: boolean = true,
    enabled: boolean = true,
) => {
    const user = useAuthedUser();
    const [storedActiveBills, storeActiveBills] = useAtom(activeBillsAtom);

    const {
        data: fetchedActiveBills,
        isLoading,
        isStale,
        refetch,
    } = useGetBills(
        {
            membershipId: user?.membership?.id ?? '',
            billActivationStatus: BillActivationStatus.ACTIVE,
        },
        {
            enabled: user?.membership?.id !== undefined && enabled,
            staleTime: useStaleTime ? 1000 * 60 * 2 : 0,
        },
    );

    useEffect(() => {
        if (fetchedActiveBills) {
            storeActiveBills(completeFetchedBills(fetchedActiveBills));
        }
    }, [fetchedActiveBills]);

    const refetchBills = useCallback(
        (force?: boolean) => {
            if (!isLoading && (force ?? isStale)) {
                void refetch();
            }
        },
        [refetch, isLoading, isStale],
    );

    return {
        activeBills: storedActiveBills,
        isLoadingActiveBills: isLoading,
        refetchBills,
    };
};

export const useGetAllBills = () => {
    const user = useAuthedUser();
    const [allBills, setAllBills] = useAtom(allBillsAtom);

    const {
        data: fetchedAllBills,
        isLoading,
        isStale,
        refetch,
    } = useGetBills(
        {
            membershipId: user?.membership?.id ?? '',
        },
        {
            enabled: user?.membership?.id !== undefined,
            refetchInterval: 1000 * 30,
            refetchIntervalInBackground: true,
        },
    );

    useEffect(() => {
        if (fetchedAllBills) {
            setAllBills(completeFetchedBills(fetchedAllBills));
        }
    }, [fetchedAllBills]);

    const refetchBills = useCallback(
        (force?: boolean) => {
            if (!isLoading && (force ?? isStale)) {
                void refetch();
            }
        },
        [refetch, isLoading, isStale],
    );

    return {
        allBills,
        isLoadingAllBills: isLoading,
        refetchBills,
    };
};
